import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Clock, MapPin, User, CreditCard, Check } from "lucide-react";
import { format } from "date-fns";
import { Seo } from "@/components/seo/Seo";

const BookingSummary = () => {
  const { companyId, serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { t } = useI18n();
  const [isProcessing, setIsProcessing] = useState(false);

  const bookingData = location.state?.bookingData;

  if (!bookingData) {
    navigate(`/booking/${companyId}/${serviceId}`);
    return null;
  }

  // Mock data
  const company = {
    id: "1",
    name: "Crystal Clean Pro",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400",
  };

  const service = {
    id: "s1",
    name: "Deep House Cleaning",
    basePrice: 150,
    description: "Complete deep clean of all rooms including bathrooms, kitchen, and living areas",
  };

  const pricing = {
    servicePrice: service.basePrice,
    tax: Math.round(service.basePrice * 0.08), // 8% tax
    discount: 0,
    total: service.basePrice + Math.round(service.basePrice * 0.08),
  };

  const handleConfirmPayment = async () => {
    if (!acceptedTerms) return;

    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      navigate("/payment-success", {
        state: {
          bookingData,
          company,
          service,
          pricing,
          orderId: `ORD-${Date.now()}`,
        }
      });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Riepilogo prenotazione | Halomora" noindex canonicalPath="/booking-summary" />
      {/* Header */}
      <div className="app-hero py-4">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/booking/${companyId}/${serviceId}`)}
            className="text-primary-foreground hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('summary.back_to_form','Torna al modulo di prenotazione')}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-8">{t('summary.title','Riepilogo prenotazione')}</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Service Details */}
              <Card className="bg-gradient-card border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-foreground">{t('summary.service_details','Dettagli servizio')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    <img
                      src={company.image}
                      alt={company.name}
                      className="w-16 h-16 rounded-xl object-cover shadow-medium"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground">{service.name}</h3>
                      <p className="text-muted-foreground mt-1">{service.description}</p>
                      <Badge variant="secondary" className="mt-2">{company.name}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Date & Time */}
              <Card className="bg-gradient-card border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {t('summary.date_time','Data e ora')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">
                        {format(bookingData.date, "EEEE, MMMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">{bookingData.time}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card className="bg-gradient-card border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {t('summary.contact_info','Informazioni di contatto')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('field.name','Nome')}</p>
                      <p className="font-medium text-foreground">{bookingData.firstName} {bookingData.lastName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground">{bookingData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('field.phone','Telefono')}</p>
                      <p className="font-medium text-foreground">{bookingData.phone}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address Information */}
              <Card className="bg-gradient-card border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    {t('summary.address_info','Indirizzi')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('field.service_address','Indirizzo del servizio')}</p>
                    <p className="font-medium text-foreground">{bookingData.serviceAddress}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('field.billing_address','Indirizzo di fatturazione')}</p>
                    <p className="font-medium text-foreground">{bookingData.billingAddress}</p>
                  </div>
                  {bookingData.notes && (
                    <div>
                      <p className="text-sm text-muted-foreground">{t('field.notes_special','Note speciali')}</p>
                      <p className="font-medium text-foreground">{bookingData.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Summary */}
            <div className="lg:col-span-1">
              <Card className="bg-gradient-card border-0 shadow-strong sticky top-8">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t('summary.payment','Riepilogo pagamento')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('summary.price_service','Prezzo servizio')}</span>
                      <span className="font-medium text-foreground">{formatCurrency(pricing.servicePrice)}</span>
                    </div>
                    {pricing.discount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('summary.discount','Sconto')}</span>
                        <span className="font-medium text-success">-{formatCurrency(pricing.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('summary.tax','Tasse')}</span>
                      <span className="font-medium text-foreground">{formatCurrency(pricing.tax)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold text-foreground">{t('summary.total','Totale')}</span>
                      <span className="font-bold text-primary">{formatCurrency(pricing.total)}</span>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="mt-6">
                    <p className="text-sm font-medium text-foreground mb-3">{t('summary.payment_method','Metodo di pagamento')}</p>
                    <div className="p-3 border rounded-lg border-border">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">**** **** **** 1234</p>
                          <p className="text-sm text-muted-foreground">Expires 12/25</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="mt-6">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="terms"
                        checked={acceptedTerms}
                        onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                      />
                      <Label htmlFor="terms" className="text-sm leading-5">
                        {t('summary.terms_prefix','Accetto i')}{" "}
                        <button className="text-primary hover:underline">
                          {t('summary.terms','Termini e Condizioni')}
                        </button>{" "}
                        {t('summary.and','e')}{" "}
                        <button className="text-primary hover:underline">
                          {t('summary.privacy','Informativa sulla Privacy')}
                        </button>
                      </Label>
                    </div>
                  </div>

                  {/* Confirm Button */}
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={!acceptedTerms || isProcessing}
                    variant="hero"
                    size="lg"
                    className="w-full mt-6"
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {t('summary.processing','Elaborazione...')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        {t('summary.confirm_pay','Conferma e paga')} {formatCurrency(pricing.total)}
                      </div>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;